// frontend/src/lib/optionalAnalytics.js
import { supabase } from "../../supabaseClient";

import {
  CONSENT_PURPOSES,
  clearConsentCache,
  hasMyConsent,
} from "../legal/consent";

const ANALYTICS_PURPOSE =
  CONSENT_PURPOSES.OPTIONAL_ANALYTICS;

const ALLOWED_EVENTS = new Set([
  "page_view",
  "feature_used",
  "ui_error",
  "onboarding_step_view",
  "lesson_started",
  "lesson_completed",
]);

const SENSITIVE_KEY_PATTERN =
  /(email|phone|mobile|password|token|secret|answer|free.?text|name|address|id.?number|registration.?number|bank|card|account.?number)/i;

const ANALYTICS_STORAGE_KEY_PATTERN =
  /(^|_)(ga|gid|gat|fbp|fbc|mixpanel|posthog|amplitude|segment|ajs)(_|$)/i;

function sanitizeValue(value, depth = 0) {
  if (depth > 2) return undefined;

  if (
    value === null ||
    typeof value === "boolean" ||
    typeof value === "number"
  ) {
    return value;
  }

  if (typeof value === "string") {
    return value.slice(0, 200);
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, 20)
      .map((item) => sanitizeValue(item, depth + 1))
      .filter((item) => item !== undefined);
  }

  if (
    value &&
    typeof value === "object" &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    return sanitizeProperties(value, depth + 1);
  }

  return undefined;
}

function sanitizeProperties(properties, depth = 0) {
  if (
    !properties ||
    typeof properties !== "object" ||
    Array.isArray(properties)
  ) {
    return {};
  }

  const cleaned = {};

  Object.entries(properties)
    .slice(0, 40)
    .forEach(([key, value]) => {
      const normalizedKey = String(key)
        .trim()
        .slice(0, 80);

      if (!normalizedKey) return;
      if (SENSITIVE_KEY_PATTERN.test(normalizedKey)) return;

      const cleanedValue = sanitizeValue(value, depth);

      if (cleanedValue !== undefined) {
        cleaned[normalizedKey] = cleanedValue;
      }
    });

  const serialized = JSON.stringify(cleaned);

  if (serialized.length > 8_000) {
    return {};
  }

  return cleaned;
}

function cleanPagePath(value) {
  const path = String(
    value ||
      (typeof window !== "undefined"
        ? window.location.pathname
        : "")
  );

  return path
    .split("?")[0]
    .split("#")[0]
    .slice(0, 500);
}

function expireCookie(name, domain = "") {
  if (typeof document === "undefined") return;

  const domainPart = domain
    ? `; domain=${domain}`
    : "";

  document.cookie =
    `${name}=; Max-Age=0; path=/; SameSite=Lax${domainPart}`;
}

function clearAnalyticsCookies() {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined"
  ) {
    return;
  }

  const cookieNames = document.cookie
    .split(";")
    .map((part) => part.split("=")[0].trim())
    .filter(Boolean)
    .filter((name) =>
      ANALYTICS_STORAGE_KEY_PATTERN.test(name)
    );

  const hostname = window.location.hostname;
  const hostnameParts = hostname.split(".");

  const rootDomain =
    hostnameParts.length >= 2
      ? `.${hostnameParts.slice(-2).join(".")}`
      : "";

  cookieNames.forEach((name) => {
    expireCookie(name);

    if (hostname && hostname !== "localhost") {
      expireCookie(name, hostname);
      expireCookie(name, `.${hostname}`);
    }

    if (rootDomain) {
      expireCookie(name, rootDomain);
    }
  });
}

function clearMatchingStorage(storage) {
  if (!storage) return;

  const keysToDelete = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (
      key &&
      ANALYTICS_STORAGE_KEY_PATTERN.test(key)
    ) {
      keysToDelete.push(key);
    }
  }

  keysToDelete.forEach((key) => {
    storage.removeItem(key);
  });
}

function denyLoadedProviderStorage() {
  if (typeof window === "undefined") return;

  const measurementId =
    process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (measurementId) {
    window[`ga-disable-${measurementId}`] = true;
  }

  // This does not load Google Analytics.
  // It only disables it if another file already loaded it.
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

function allowLoadedProviderStorage() {
  if (typeof window === "undefined") return;

  const measurementId =
    process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (measurementId) {
    window[`ga-disable-${measurementId}`] = false;
  }

  // Again, this does not load a provider.
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
    });
  }
}

export function clearOptionalAnalyticsStorage() {
  if (typeof window === "undefined") return;

  denyLoadedProviderStorage();
  clearAnalyticsCookies();

  try {
    clearMatchingStorage(window.localStorage);
  } catch {}

  try {
    clearMatchingStorage(window.sessionStorage);
  } catch {}
}

export async function canUseOptionalAnalytics({
  forceRefresh = false,
} = {}) {
  const allowed = await hasMyConsent(
    ANALYTICS_PURPOSE,
    { forceRefresh }
  );

  if (!allowed) {
    clearOptionalAnalyticsStorage();
  }

  return allowed;
}

export async function recordOptionalAnalyticsEvent(
  eventName,
  properties = {},
  {
    pagePath,
  } = {}
) {
  const normalizedEventName = String(
    eventName || ""
  )
    .trim()
    .toLowerCase();

  if (!ALLOWED_EVENTS.has(normalizedEventName)) {
    throw new Error(
      `Unsupported analytics event: ${normalizedEventName}`
    );
  }

  const allowed =
    await canUseOptionalAnalytics();

  if (!allowed) {
    return {
      recorded: false,
      reason: "consent_not_granted",
    };
  }

  const safeProperties =
    sanitizeProperties(properties);

  const { data, error } = await supabase.rpc(
    "record_my_optional_analytics_event",
    {
      p_event_name: normalizedEventName,
      p_properties: safeProperties,
      p_page_path: cleanPagePath(pagePath),
    }
  );

  if (error) {
    console.error(
      "Optional analytics event rejected:",
      error
    );

    return {
      recorded: false,
      reason: "database_rejected",
    };
  }

  return data || {
    recorded: false,
  };
}

export function installOptionalAnalyticsRuntime() {
  if (typeof window === "undefined") {
    return () => {};
  }

  let active = true;

  async function refreshConsent(event) {
    const purposeKey =
      event?.detail?.purposeKey;

    if (
      purposeKey &&
      purposeKey !== ANALYTICS_PURPOSE
    ) {
      return;
    }

    clearConsentCache(ANALYTICS_PURPOSE);

    const allowed =
      await canUseOptionalAnalytics({
        forceRefresh: true,
      });

    if (!active) return;

    if (allowed) {
      allowLoadedProviderStorage();
    } else {
      clearOptionalAnalyticsStorage();
    }
  }

  window.addEventListener(
    "gwin:consent-changed",
    refreshConsent
  );

  refreshConsent();

  return () => {
    active = false;

    window.removeEventListener(
      "gwin:consent-changed",
      refreshConsent
    );
  };
}